import React, { useState, useRef } from 'react';
import Icon from './Icon';
import styled from 'styled-components';
import agentService from '../services/agentService';

/**
 * Bringing a file of leads into the ledger.
 *
 * This used to live at the bottom of the audience page, under "Upload Lead List
 * (Optional)" — the last place anybody would look for "bring in my database",
 * and a step in a wizard rather than something you do on a Tuesday. It belongs
 * on Leads, next to the leads.
 *
 * The flow is unchanged and deliberately three beats: read the file, show what
 * Orion made of the columns, then import. The middle beat is the point — a
 * mapping is a guess, and the only moment a person can correct it cheaply is
 * before the rows land.
 */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(6, 8, 20, 0.72);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 3rem 1.5rem;
  overflow-y: auto;
  z-index: 1200;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 720px;
  background: var(--gradient-card, #12172b);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.6rem;
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.4rem;
`;

const Heading = styled.h2`
  font-family: var(--font-heading);
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const Close = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.2rem;

  &:hover { color: var(--text-primary); }
`;

const Lede = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0 0 1.2rem;
`;

const Drop = styled.div`
  border: 1px dashed ${(p) => (p.$over ? 'var(--accent-cyan)' : 'var(--border-color)')};
  background: ${(p) => (p.$over ? 'rgba(34,211,238,0.06)' : 'transparent')};
  border-radius: var(--radius-md);
  padding: 2.2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  color: var(--text-secondary);

  &:hover { border-color: var(--accent-cyan); }
`;

const DropTitle = styled.div`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
`;

const Small = styled.div`
  color: var(--text-muted);
  font-size: 0.82rem;
  line-height: 1.5;
`;

const MapRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  &:first-of-type { border-top: none; }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 0.35rem;
  }
`;

const Column = styled.div`
  color: var(--text-primary);
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sample = styled.div`
  color: var(--text-muted);
  font-size: 0.76rem;
  margin-top: 0.12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Arrow = styled.div`
  color: var(--text-muted);

  @media (max-width: 620px) { display: none; }
`;

const Select = styled.select`
  width: 100%;
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.5rem 0.7rem;
  color: ${(p) => (p.$ignored ? 'var(--text-muted)' : 'var(--text-primary)')};
  font-size: 0.86rem;

  &:focus { outline: none; border-color: var(--accent-cyan); }
  option { background: var(--primary-bg); color: var(--text-primary); }
`;

const Scroll = styled.div`
  max-height: 46vh;
  overflow-y: auto;
  margin: 0.4rem 0 1rem;
  padding-right: 0.3rem;
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
`;

const Button = styled.button`
  border-radius: var(--radius-md);
  padding: 0.65rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  background: ${(p) => (p.$primary ? 'var(--accent-cyan)' : 'transparent')};
  color: ${(p) => (p.$primary ? 'var(--primary-bg)' : 'var(--text-secondary)')};
  border-color: ${(p) => (p.$primary ? 'transparent' : 'var(--border-color)')};

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):hover { filter: brightness(1.1); }
`;

const Result = styled.div`
  border: 1px solid rgba(34, 211, 238, 0.4);
  background: rgba(34, 211, 238, 0.06);
  border-radius: var(--radius-md);
  padding: 1.1rem 1.2rem;
  line-height: 1.6;
  color: var(--text-primary);
`;

const Problem = styled.div`
  border: 1px solid rgba(248, 113, 113, 0.4);
  background: rgba(248, 113, 113, 0.07);
  border-radius: var(--radius-md);
  padding: 0.9rem 1.1rem;
  margin-top: 1rem;
  color: var(--text-secondary);
  line-height: 1.55;
`;

const Tag = styled.code`
  display: inline-block;
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 4px;
  padding: 0.08rem 0.4rem;
  margin: 0.15rem 0.25rem 0 0;
  font-family: var(--font-mono, monospace);
  font-size: 0.78rem;
  color: var(--text-secondary);
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 0.9rem;
`;

const Part = styled.div``;

const PartTitle = styled.div`
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
`;

function LeadImportModal({ projectId, onClose, onImported }) {
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [fileName, setFileName] = useState('');
  const [progress, setProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(null);
  // Held separately: the result is shown after the preview is gone, and it
  // still needs to name the fields it understood.
  const [importedFields, setImportedFields] = useState([]);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const input = useRef(null);

  const read = async (file) => {
    if (!file) return;
    if (!/\.(csv|xls|xlsx)$/i.test(file.name)) {
      setError('Orion reads CSV and Excel files. Export from your tool and try again.');
      return;
    }

    setError('');
    setImported(null);
    setFileName(file.name);
    setProgress(1);

    try {
      const uploaded = await agentService.uploadLeadFile(projectId, file, (e) => {
        if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
      });
      const key = uploaded.data && uploaded.data.file && uploaded.data.file.key;
      const proposed = await agentService.previewLeadFile(projectId, key);
      setPreview(proposed.data);
      setMapping(proposed.data.mapping || {});
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not read that file');
      setFileName('');
    } finally {
      setProgress(0);
    }
  };

  /**
   * A field can only come from one column, except the ones that legitimately
   * span several — a location split into city, state and country is one field
   * assembled from three, and forcing a choice there loses two thirds of it.
   */
  const chooseField = (header, field) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!field) { delete next[header]; return next; }
      if (!multiColumn.has(field)) {
        for (const [key, value] of Object.entries(next)) {
          if (value === field && key !== header) delete next[key];
        }
      }
      next[header] = field;
      return next;
    });
  };

  const run = async () => {
    try {
      setImporting(true);
      setError('');
      const result = await agentService.importLeadFile(projectId, preview.fileKey, mapping);
      setImportedFields(preview.fields || []);
      setImported(result.data);
      if (onImported) onImported(result.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not import that file');
    } finally {
      setImporting(false);
    }
  };

  const fields = (preview && preview.fields) || [];
  const multiColumn = new Set(fields.filter((f) => f.multi).map((f) => f.value));
  const mapped = preview ? Object.values(mapping).filter(Boolean) : [];
  const kept = preview ? preview.headers.filter((h) => !mapping[h]) : [];

  return (
    <Backdrop onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Import leads from a file">
        <Top>
          <Heading>{imported ? 'Imported' : 'Import leads'}</Heading>
          <Close onClick={onClose} aria-label="Close">×</Close>
        </Top>

        {imported ? (
          <>
            <Result>
              <strong>
                {imported.imported} lead{imported.imported === 1 ? '' : 's'} imported
              </strong>
              {imported.list ? <> into <strong>{imported.list.name}</strong></> : null}.
              {' '}
              {imported.withEmail} came with an email
              {imported.withoutEmail ? `, ${imported.withoutEmail} without one` : ''}.
              {imported.duplicates
                ? ` ${imported.duplicates} duplicate row${imported.duplicates === 1 ? '' : 's'} were folded together.`
                : ''}
              {imported.skipped && imported.skipped.length
                ? ` ${imported.skipped.length} row${imported.skipped.length === 1 ? '' : 's'} were skipped — ${imported.skipped[0].reason.toLowerCase()}.`
                : ''}

              {/* Importing records people; it deliberately does not go and buy
                  addresses, because that costs real credits per lead. Say where
                  the priced action is rather than leaving a silent gap. */}
              {imported.withoutEmail > 0 && (
                <Small style={{ marginTop: '0.7rem' }}>
                  Orion did not look up the {imported.withoutEmail} missing address
                  {imported.withoutEmail === 1 ? '' : 'es'} — that spends credits, so it is
                  yours to start. Select those leads and use <strong>Find contact details</strong>.
                </Small>
              )}

              <Split>
                <Part>
                  <PartTitle>{mapped.length} columns understood</PartTitle>
                  {(importedFields || []).filter((f) => mapped.includes(f.value)).map((f) => (
                    <Tag key={f.value}>{f.label}</Tag>
                  ))}
                </Part>
                {imported.list && imported.list.extraFields && imported.list.extraFields.length > 0 && (
                  <Part>
                    <PartTitle>{imported.list.extraFields.length} kept as extra fields</PartTitle>
                    {imported.list.extraFields.map((field) => <Tag key={field}>{field}</Tag>)}
                  </Part>
                )}
              </Split>
            </Result>

            {imported.list && imported.list.extraFields && imported.list.extraFields.length > 0 && (
              <Small style={{ marginTop: '0.8rem' }}>
                Kept fields are stored with each person and shown when you open them.
                If something in that list should have been understood — a phone
                number, say — import the file again and map that column.
              </Small>
            )}

            <Foot style={{ marginTop: '1.2rem' }}>
              <span />
              <Button $primary onClick={onClose}>Done</Button>
            </Foot>
          </>
        ) : !preview ? (
          <>
            <Lede>
              A CSV or Excel export from anywhere — Apollo, Sales Navigator, a
              spreadsheet somebody keeps by hand. Orion works out what the columns
              mean and shows you before importing anything.
            </Lede>

            <Drop
              $over={dragOver}
              onClick={() => input.current && input.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); read(e.dataTransfer.files[0]); }}
            >
              <input
                ref={input}
                type="file"
                accept=".csv,.xls,.xlsx"
                style={{ display: 'none' }}
                onChange={(e) => { read(e.target.files[0]); e.target.value = ''; }}
              />
              {progress > 0 ? (
                <>
                  <DropTitle>Reading {fileName}…</DropTitle>
                  <Small>{progress}%</Small>
                </>
              ) : (
                <>
                  <DropTitle>Drop a file here, or click to choose one</DropTitle>
                  <Small>CSV, XLS or XLSX</Small>
                </>
              )}
            </Drop>

            {error && <Problem>{error}</Problem>}
          </>
        ) : (
          <>
            <Lede>
              {preview.mappedBy === 'model'
                ? 'Orion worked these out from your headers and the first few rows. Correct anything that looks wrong — this is the cheap moment to do it.'
                : 'Recognised from the column names. Correct anything that looks wrong.'}
            </Lede>

            <Scroll>
              {preview.headers.map((header) => (
                <MapRow key={header}>
                  <div style={{ minWidth: 0 }}>
                    <Column title={header}>{header}</Column>
                    {preview.sample && preview.sample[0] && preview.sample[0][header] ? (
                      <Sample title={String(preview.sample[0][header])}>
                        {String(preview.sample[0][header])}
                      </Sample>
                    ) : null}
                  </div>
                  <Arrow><Icon name="arrowRight" size={14} /></Arrow>
                  <Select
                    value={mapping[header] || ''}
                    $ignored={!mapping[header]}
                    onChange={(e) => chooseField(header, e.target.value)}
                    aria-label={`What ${header} means`}
                  >
                    <option value="">Keep as an extra field</option>
                    {fields.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </Select>
                </MapRow>
              ))}
            </Scroll>

            <Small>
              {kept.length > 0
                ? `${mapped.length} column${mapped.length === 1 ? '' : 's'} understood · ${kept.length} kept as extra fields, stored with each person and visible when you open them.`
                : `All ${mapped.length} columns understood.`}
            </Small>

            <Foot style={{ marginTop: '1rem' }}>
              <Small>
                {preview.importable} of {preview.totalRows} rows can be imported
                {preview.skipped && preview.skipped.length
                  ? ` · ${preview.skipped.length} skipped (${preview.skipped[0].reason.toLowerCase()})`
                  : ''}
              </Small>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <Button onClick={() => { setPreview(null); setFileName(''); }}>Different file</Button>
                <Button $primary onClick={run} disabled={importing || !preview.importable}>
                  {importing ? 'Importing…' : `Import ${preview.importable} lead${preview.importable === 1 ? '' : 's'}`}
                </Button>
              </div>
            </Foot>

            {error && <Problem>{error}</Problem>}
          </>
        )}
      </Sheet>
    </Backdrop>
  );
}

export default LeadImportModal;
